import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) {
      toast.success(t("auth.welcomeBack"));
      nav("/", { replace: true });
    } else {
      toast.error(r.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1.1fr] bg-background">
      <div className="flex flex-col p-10 lg:p-14 relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
              <Download strokeWidth={1.75} className="h-4 w-4" />
            </div>
            <div>
            <div className="text-sm font-semibold tracking-tight">Ads Library</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Downloader</div>
            </div>
          </div>
          <LanguageSwitcher />
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm mx-auto">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">
              {t("auth.signInTitle")}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("auth.signInSubtitle")}
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  {t("auth.email")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  {t("auth.password")}
                </Label>
                <Input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                />
              </div>
              <Button type="submit" disabled={busy} className="w-full" data-testid="login-submit">
                {busy ? t("common.loading") : t("auth.signIn")}
              </Button>
              <p className="text-sm text-muted-foreground text-center">
                {t("auth.noAccount")}{" "}
                <Link to="/register" className="text-foreground hover-underline" data-testid="link-register">
                  {t("auth.createOne")}
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden bg-[#07080A]">
        <img
          src="https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(94,106,210,0.25),transparent_60%)]" />
        <div className="relative h-full flex flex-col justify-end p-14 text-white">
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-[0.28em] text-white/60">Meta Ad Library · Assets</div>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight">Baixe cada criativo. Organize por marca.</h2>
            <p className="mt-3 text-sm text-white/70">
              Extensão + dashboard para inteligência competitiva sobre anúncios públicos da Meta.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
